import { ActionTypeKeys as CommonActionTypeKeys, ActionTypes as CommonActionTypes } from 'embed/actions'
import {
  AprType,
  LifeInsuranceCoverageTier,
  LifeInsuranceRatesFilter,
  LoanOfferProductSubType,
  LoanRatesFilter,
} from 'embed/constants'

import { Product } from '@evenfinancial/even-ts-static'
import { DeepReadonly } from 'utility-types'

export interface RatesProductFiltersState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any[]
}

export type RatesFiltersState = DeepReadonly<{
  [Product.CreditCard]: RatesProductFiltersState
  [Product.Deposits]: RatesProductFiltersState
  [Product.LifeInsurance]: {
    [LifeInsuranceRatesFilter.CoverageTier]: LifeInsuranceCoverageTier[]
  }
  [Product.Loan]: RatesProductFiltersState
  [Product.Other]: RatesProductFiltersState
}>

export const ratesFiltersInitialState: RatesFiltersState = {
  [Product.CreditCard]: {},
  [Product.Deposits]: {},
  [Product.LifeInsurance]: {
    [LifeInsuranceRatesFilter.CoverageTier]: [LifeInsuranceCoverageTier.Essential],
  },
  [Product.Loan]: {
    [LoanRatesFilter.AprType]: [AprType.Fixed],
    [LoanRatesFilter.ProductSubType]: [
      LoanOfferProductSubType.PersonalLoan,
      LoanOfferProductSubType.SecuredLoan,
      LoanOfferProductSubType.StudentLoanRefinance,
      LoanOfferProductSubType.LineOfCredit,
      LoanOfferProductSubType.AutomobileRefinance,
      LoanOfferProductSubType.HomeEquityLineOfCredit,
    ],
  },
  // TODO V1-1823 remove the unknown type
  [Product.Other]: {
    [LoanRatesFilter.ProductSubType]: [LoanOfferProductSubType.DebtRelief, LoanOfferProductSubType.Unknown],
  },
}

const reducer = (state: RatesFiltersState = ratesFiltersInitialState, action: CommonActionTypes): RatesFiltersState => {
  switch (action.type) {
    case CommonActionTypeKeys.RATES_FILTERS_CLEARED: {
      const nextState = { ...state }
      if (action.filterType) {
        if (action.filterValue) {
          // clear a filter for a given filter type
          nextState[action.product][action.filterType] = nextState[action.product][action.filterType].filter(
            (f) => f !== action.filterValue,
          )
        } else {
          // clear all filters for a given filter type
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete nextState[action.product][action.filterType]
        }
      } else {
        // clear all filters for a given product
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete nextState[action.product]
      }
      return nextState
    }
    case CommonActionTypeKeys.RATES_FILTER_REPLACED: {
      // for updating mutually exclusive filter types
      const nextState = { ...state }
      nextState[action.product][action.filterType] = [action.filterValue]
      return nextState
    }
    case CommonActionTypeKeys.RATES_FILTER_SET: {
      // for pushing filters to a given filter type, preserves filter value if already present
      const nextState = { ...state }
      const currentFilters = nextState[action.product][action.filterType]
      const alreadyPresent = currentFilters.includes(action.filterValue)
      nextState[action.product][action.filterType] = alreadyPresent
        ? currentFilters
        : [...currentFilters, action.filterValue]
      return nextState
    }
    case CommonActionTypeKeys.RATES_FILTER_TOGGLED: {
      // for pushing filters to a given filter type, removes filter value if already present
      const nextState = { ...state }
      const alreadyPresent = nextState[action.product][action.filterType].includes(action.filterValue)
      const preservedFilters = nextState[action.product][action.filterType].filter((f) => f !== action.filterValue)

      nextState[action.product][action.filterType] = alreadyPresent
        ? preservedFilters
        : [...preservedFilters, action.filterValue]
      return nextState
    }
    default:
      return state
  }
}

export default reducer
